import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import API_BASE_URL from '../config/api';
import { toast } from 'react-toastify';

export default function ScheduleDayDialog({ open, date, dayType, onClose, onAction }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDateString = (date) => {
    // Use local date to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDeclareHoliday = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/schedule/holiday`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          date: getDateString(date),
          reason: reason || null,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Holiday declared successfully');
        onAction();
      } else {
        toast.error(data.message || 'Failed to declare holiday');
      }
    } catch (error) {
      console.error('Error declaring holiday:', error);
      toast.error('Error declaring holiday');
    } finally {
      setLoading(false);
    }
  };

  const handleUndeclareHoliday = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/schedule/holiday/${getDateString(date)}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success('Holiday undeclared successfully');
        onAction();
      } else {
        toast.error(data.message || 'Failed to undeclare holiday');
      }
    } catch (error) {
      console.error('Error undeclaring holiday:', error);
      toast.error('Error undeclaring holiday');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSunday = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/schedule/open-sunday`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          date: getDateString(date),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Sunday opened for work successfully');
        onAction();
      } else {
        toast.error(data.message || 'Failed to open Sunday');
      }
    } catch (error) {
      console.error('Error opening Sunday:', error);
      toast.error('Error opening Sunday');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSunday = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/schedule/open-sunday/${getDateString(date)}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success('Sunday closed successfully');
        onAction();
      } else {
        toast.error(data.message || 'Failed to close Sunday');
      }
    } catch (error) {
      console.error('Error closing Sunday:', error);
      toast.error('Error closing Sunday');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (dayType) {
      case 'WorkingDay':
        return (
          <>
            <DialogContent sx={{ bgcolor: '#2d2d2d', color: '#fff' }}>
              <Typography variant="body1" sx={{ mb: 2, color: '#fff' }}>
                {formatDate(date)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#ccc' }}>
                Declare this day as a holiday? This will reduce expected working hours
                without penalizing staff salary.
              </Typography>
              <TextField
                fullWidth
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Diwali, Festival, etc."
                sx={{ 
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#1a1a1a',
                    color: '#fff',
                    '& fieldset': {
                      borderColor: '#444',
                    },
                    '&:hover fieldset': {
                      borderColor: '#C4A574',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#C4A574',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#ccc',
                    '&.Mui-focused': {
                      color: '#C4A574',
                    },
                  },
                  '& .MuiOutlinedInput-input::placeholder': {
                    color: '#888',
                  },
                }}
              />
            </DialogContent>
            <DialogActions sx={{ bgcolor: '#2d2d2d', borderTop: '1px solid #444', p: 2 }}>
              <Button 
                onClick={onClose} 
                disabled={loading}
                sx={{ 
                  color: '#ccc',
                  '&:hover': { bgcolor: '#444' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeclareHoliday}
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: '#d32f2f',
                  color: '#fff',
                  '&:hover': { bgcolor: '#c62828' },
                  '&:disabled': { bgcolor: '#555', color: '#888' },
                }}
              >
                Declare Holiday
              </Button>
            </DialogActions>
          </>
        );

      case 'Holiday':
        return (
          <>
            <DialogContent sx={{ bgcolor: '#2d2d2d', color: '#fff' }}>
              <Typography variant="body1" sx={{ mb: 2, color: '#fff' }}>
                {formatDate(date)}
              </Typography>
              <Box sx={{ bgcolor: '#4a1a1a', p: 2, borderRadius: 1, mb: 2, border: '1px solid #d32f2f' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#ffcdd2' }}>
                  Currently: Holiday
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                Undeclare this holiday? This will restore it as a working day and
                increase expected working hours.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ bgcolor: '#2d2d2d', borderTop: '1px solid #444', p: 2 }}>
              <Button 
                onClick={onClose} 
                disabled={loading}
                sx={{ 
                  color: '#ccc',
                  '&:hover': { bgcolor: '#444' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUndeclareHoliday}
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: '#2e7d32',
                  color: '#fff',
                  '&:hover': { bgcolor: '#1b5e20' },
                  '&:disabled': { bgcolor: '#555', color: '#888' },
                }}
              >
                Undeclare Holiday
              </Button>
            </DialogActions>
          </>
        );

      case 'DefaultSunday':
        return (
          <>
            <DialogContent sx={{ bgcolor: '#2d2d2d', color: '#fff' }}>
              <Typography variant="body1" sx={{ mb: 2, color: '#fff' }}>
                {formatDate(date)}
              </Typography>
              <Box sx={{ bgcolor: '#2d1b3d', p: 2, borderRadius: 1, mb: 2, border: '1px solid #9c27b0' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#e1bee7' }}>
                  Currently: Sunday (Holiday)
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                Open this Sunday for work? Staff can mark attendance, but it won't
                increase mandatory working hours.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ bgcolor: '#2d2d2d', borderTop: '1px solid #444', p: 2 }}>
              <Button 
                onClick={onClose} 
                disabled={loading}
                sx={{ 
                  color: '#ccc',
                  '&:hover': { bgcolor: '#444' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleOpenSunday}
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: '#f57c00',
                  color: '#fff',
                  '&:hover': { bgcolor: '#e65100' },
                  '&:disabled': { bgcolor: '#555', color: '#888' },
                }}
              >
                Open for Work
              </Button>
            </DialogActions>
          </>
        );

      case 'OpenSunday':
        return (
          <>
            <DialogContent sx={{ bgcolor: '#2d2d2d', color: '#fff' }}>
              <Typography variant="body1" sx={{ mb: 2, color: '#fff' }}>
                {formatDate(date)}
              </Typography>
              <Box sx={{ bgcolor: '#3d3a1a', p: 2, borderRadius: 1, mb: 2, border: '1px solid #C4A574' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff9c4' }}>
                  Currently: Opened Sunday
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                Close this Sunday? This will restore it to a holiday and prevent
                staff from marking attendance.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ bgcolor: '#2d2d2d', borderTop: '1px solid #444', p: 2 }}>
              <Button 
                onClick={onClose} 
                disabled={loading}
                sx={{ 
                  color: '#ccc',
                  '&:hover': { bgcolor: '#444' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCloseSunday}
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: '#757575',
                  color: '#fff',
                  '&:hover': { bgcolor: '#616161' },
                  '&:disabled': { bgcolor: '#555', color: '#888' },
                }}
              >
                Close (Make Holiday)
              </Button>
            </DialogActions>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#2d2d2d',
          border: '1px solid #444',
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: '#2d2d2d', 
        fontWeight: 600,
        color: '#C4A574',
        borderBottom: '1px solid #444',
      }}>
        Manage Schedule
      </DialogTitle>
      {renderContent()}
    </Dialog>
  );
}
